import { EyeIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { PdfRender } from 'components/PdfRender'
import { PlainInput2 } from 'components/PlainInput/PlainInput2'
import { LoanSubmissionDocument } from 'pages/LoanSubmission/types'
import { useEffect, useState } from 'react'
import { downloadS3Documents } from 'services'
import { Modal, Tooltip } from 'stories/components'
import { Input2 } from 'stories/components/Input2/Input2'
import { confirm } from 'utils'

import { DocumentSection } from './types'

export const ExtractDocumentSectionsModal = ({
  sections,
  document,
  onClose,
  onSubmit,
}: {
  sections: DocumentSection[]
  document: LoanSubmissionDocument
  onClose: Function
  onSubmit: Function
}) => {
  const [isLoading, setLoading] = useState(true)
  const [editableSections, setEditableSections] = useState<DocumentSection[]>([...sections])
  const [fileUrl, setFileUrl] = useState<string | null>(null)
  const [selectedSectionIndex, setSelectedSectionIndex] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    downloadS3Documents(document.key)
      .then(({ url }) => setFileUrl(url))
      .finally(() => setLoading(false))
  }, [])

  const updateSection = (index: number, field: keyof DocumentSection, value: string | number) => {
    const updated = [...editableSections]
    updated[index] = { ...updated[index], [field]: value }
    setEditableSections(updated)
  }

  const deleteSection = async (index: number) => {
    const result = await confirm('Are you sure you want to delete this section?')
    if (!result) return
    const updated = editableSections.filter((_, i) => i !== index)
    setEditableSections(updated)
    if (selectedSectionIndex === index) {
      setSelectedSectionIndex(null)
    } else if (selectedSectionIndex !== null && selectedSectionIndex > index) {
      setSelectedSectionIndex(selectedSectionIndex - 1)
    }
  }

  const addSectionBelow = (index: number) => {
    const newSection: DocumentSection = { title: '', pageStart: 0, pageEnd: 0 }
    const updated = [...editableSections]
    updated.splice(index + 1, 0, newSection)
    setEditableSections(updated)
  }

  const onExtractAndSave = async () => {
    setLoading(true)
    try {
      await onSubmit(editableSections)
    } finally {
      setLoading(false)
    }
  }

  const onViewSection = (index: number) => {
    setSelectedSectionIndex(selectedSectionIndex === index ? null : index)
  }

  const selectedSection = selectedSectionIndex !== null ? editableSections[selectedSectionIndex] : null

  const filteredSections = editableSections
    .map((section, originalIndex) => ({ section, originalIndex }))
    .filter(({ section }) => (section.title || '').toLowerCase().includes(searchQuery.toLowerCase()))

  return (
    <Modal
      title={`Analyze and Extract Document - ${document.name}`}
      titleCancel="Close"
      titleOkay={'Extract and Save to Document Storage'}
      disabled={!editableSections.length}
      loading={isLoading}
      isOpen
      onClose={() => onClose()}
      onOk={onExtractAndSave}
    >
      <div className="flex gap-4">
        {/* Table Section */}
        <div className="flex-1 overflow-x-auto">
          <div className="mb-3">
            <Input2
              type="text"
              title="Search by title..."
              value={searchQuery}
              onChange={(val: string) => setSearchQuery(val)}
            />
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-medium text-gray-500">
                <th className="px-4 py-3">No</th>
                <th className="px-4 py-3 w-96">Title</th>
                <th className="px-4 py-3 w-24">Page Start</th>
                <th className="px-4 py-3 w-24">Page End</th>
                <th className="px-4 py-3 w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSections.map(({ section, originalIndex }) => (
                <tr
                  key={originalIndex}
                  className={`transition-colors ${
                    selectedSectionIndex === originalIndex ? 'bg-blue-100 hover:bg-blue-100' : 'hover:bg-gray-50'
                  }`}
                >
                  <td className="px-4 py-2">{originalIndex + 1}</td>
                  <td className="px-4 py-2">
                    <PlainInput2
                      value={section.title}
                      content={section.title}
                      onChange={(val: string) => updateSection(originalIndex, 'title', val)}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <PlainInput2
                      value={String(section.pageStart)}
                      content={String(section.pageStart)}
                      type="number"
                      onChange={(val: string) => updateSection(originalIndex, 'pageStart', Number(val))}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <PlainInput2
                      value={String(section.pageEnd)}
                      content={String(section.pageEnd)}
                      type="number"
                      onChange={(val: string) => updateSection(originalIndex, 'pageEnd', Number(val))}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <Tooltip message="View Section" placement="right">
                        <button
                          type="button"
                          className={`p-1 transition-colors ${
                            selectedSectionIndex === originalIndex
                              ? 'text-blue-600'
                              : 'text-gray-500 hover:text-blue-600'
                          }`}
                          onClick={() => onViewSection(originalIndex)}
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                      </Tooltip>
                      <Tooltip message="Add New Section Below" placement="right">
                        <button
                          type="button"
                          className="p-1 text-gray-500 hover:text-green-600 transition-colors"
                          onClick={() => addSectionBelow(originalIndex)}
                        >
                          <PlusIcon className="w-4 h-4" />
                        </button>
                      </Tooltip>
                      <Tooltip message="Delete Section" placement="right">
                        <button
                          type="button"
                          className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                          onClick={() => deleteSection(originalIndex)}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredSections.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    {searchQuery ? 'No sections match your search' : 'No sections available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PDF Preview Section */}
        {selectedSection && fileUrl && (
          <div className="w-128 flex-shrink-0 border-l pl-4">
            <div className="sticky top-0">
              <div className="text-sm font-medium text-gray-700 mb-2">
                Preview: {selectedSection.title || 'Untitled Section'}
                <span className="text-gray-400 ml-2">
                  (Pages {selectedSection.pageStart} - {selectedSection.pageEnd})
                </span>
              </div>
              <div className="overflow-auto max-h-[calc(100vh-15rem)] border rounded bg-gray-100">
                <PdfRender file={fileUrl} start={selectedSection.pageStart} end={selectedSection.pageEnd} />
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
